import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  DataTable,
  ProductInformation
} from "shared/types/__generated__/graphql"
import { DataTableView } from "shared/ui/data-table/DataTableView"
import { Label } from "shared/ui/label/Label"
import {
  PropertyList,
  PropertyListProps
} from "shared/ui/property-list/PropertyList"

import { ProductAccordionDataTableOverlayButton } from "./ProductAccordionDataTableOverlayButton"

interface ProductAccordionDimensioningContentProps {
  externalDimensioning: ProductInformation["dimensioning"]
  isTooltip?: boolean
  openPhysicalTableOverlay?: () => void
}

function ProductAccordionDimensioningRoom({
  label,
  entries
}: {
  label: string
  entries: PropertyListProps["list"]
}) {
  return (
    <div className="product-accordion__room">
      <Label className="product-accordion__room-header">{label}</Label>
      <PropertyList
        hideChip
        className="product-accordion__property-list"
        list={entries}
      />
    </div>
  )
}

export const ProductAccordionDimensioningContent = React.memo(
  ({
    externalDimensioning,
    isTooltip
  }: ProductAccordionDimensioningContentProps) => {
    const { t } = useTranslation()

    const tables = useMemo<DataTable[]>(() => {
      return externalDimensioning?.physicalTables || []
    }, [externalDimensioning?.physicalTables])

    return (
      <div className="product-accordion__content">
        {externalDimensioning?.environmentalAttributes ? (
          <ProductAccordionDimensioningRoom
            label={t("Product.Configuration.VariantKeyRelatedParams")}
            entries={externalDimensioning.environmentalAttributes
              .flat()
              .map(attr => ({ label: attr.label, value: attr.value }))}
          />
        ) : null}
        {externalDimensioning?.physicalAttributes ? (
          <ProductAccordionDimensioningRoom
            label={t("Product.Configuration.PhysicalParams")}
            entries={externalDimensioning.physicalAttributes.map(attr => ({
              label: attr.label,
              value: `${attr.value}${attr.unit ? ` ${attr.unit}` : ""}`
            }))}
          />
        ) : null}

        {externalDimensioning?.supplementaryText ? (
          <span className="label">
            {externalDimensioning.supplementaryText}
          </span>
        ) : null}

        <div className="product-accordion__actions">
          {tables.map(table => (
            <ProductAccordionDataTableOverlayButton
              key={table.description}
              table={table}
              isHidden={isTooltip}
            />
          ))}
        </div>

        <div className="product-accordion__dimensioning-tables--print-only">
          {tables.map(table => (
            <div
              className="product-accordion-dimensioning-table"
              key={table.description}
            >
              <Label className="product-accordion-dimensioning-table__label">
                {table.description}
              </Label>
              <DataTableView
                id="printed-content"
                data={table}
                label={table.description ?? ""} // Print Only -- can be empty
                className="equal-columns"
              />
            </div>
          ))}
        </div>
      </div>
    )
  }
)
